import { Component, effect, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as _ from 'lodash';
import { NgIf } from '@angular/common';
import { SettingsService } from 'src/app/shared/services/settings';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';

// Declare Helpers globally
declare const Helpers: any;

@Component({
  selector: 'app-litmus-action-general',
  templateUrl: './litmus-action-general.component.html',
  styleUrls: ['./litmus-action-general.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, ReactiveFormsModule, LtmsLoaderComponent]
})
export class LitmusActionGeneralComponent implements OnInit, OnChanges, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);
  configurationLoaded: boolean = false;
  brandDetails: any = {};
  generalForm!: FormGroup;
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;
  brand: any = {};
  genralformConfig: any = {};
  private initializedBrandId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private _settingsService: SettingsService,
    private accountsService: AccountsService,
    private stateService: AppStateService,
    private helpersService: HelpersService,
    private toastService: ToastService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        if (_.isEmpty(brandDetail)) {
          return;
        }
        if (this.generalForm && this.initializedBrandId === brandDetail._id) {
          this.brandDetails = brandDetail;
          this.applyBrandDetailsToForm();
          return;
        }
        this.brandDetails = brandDetail;
        this.initializedBrandId = brandDetail._id;
        this.configurationLoaded = true;
        this.initializeForm();
      }
    })
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly']) {
      this.updateFormReadonlyState();
    }
  }

  private updateFormReadonlyState(): void {
    if (!this.generalForm) {
      return;
    }
    if (this.readonly) {
      this.generalForm.disable({ emitEvent: false });
    } else {
      this.generalForm.enable({ emitEvent: false });
    }
  }

  private initializeForm(): void {
    this.generalForm = this.fb.group({
      is_action_workflow_enabled: [this.brandDetails?.is_action_workflow_enabled || false],
      feed_attachment_enabled: [this.brandDetails?.feed_attachment_enabled || false]
    });

    // Listen to form changes with debounce and cleanup
    this.generalForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.onFormChange(value);
      });

    this.updateFormReadonlyState();
  }

  private applyBrandDetailsToForm(): void {
    if (!this.generalForm || this.isSaveEnabled) {
      return;
    }
    this.generalForm.patchValue({
      is_action_workflow_enabled: this.brandDetails?.is_action_workflow_enabled || false,
      feed_attachment_enabled: this.brandDetails?.feed_attachment_enabled || false
    }, { emitEvent: false });
    this.isSaveEnabled = false;
  }

  onFormChange(value: any): void {
    this.isSaveEnabled = this.hasFormChanged();
  }

  onCheckboxChange(field: string, value: boolean): void {
    this.isSaveEnabled = this.hasFormChanged();
  }

  /**
   * Check if form has changes compared to original configuration
   * Works with actual flag/key & value from brandDetails
   */
  private hasFormChanged(): boolean {
    if (!this.generalForm || !this.brandDetails) return false;

    const currentValues = this.generalForm.value;
    const originalValues = {
      is_action_workflow_enabled: this.brandDetails.is_action_workflow_enabled || false,
      feed_attachment_enabled: this.brandDetails.feed_attachment_enabled || false
    };

    // Compare current form values with original brand details
    const hasChanges = (
      currentValues.is_action_workflow_enabled !== originalValues.is_action_workflow_enabled ||
      currentValues.feed_attachment_enabled !== originalValues.feed_attachment_enabled
    );

    return hasChanges;
  }

  /**
   * Save the general configuration
   */
  saveConfiguration() {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.isSaving) return;

    this.isSaving = true;
    this.genralformConfig = this.generalForm.value

    this._settingsService.updateBrandDashboardConfiguration(this.brandDetails._id, this.genralformConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (res && res.code == 0) {
            this.toastService.showPopup("SUCCESS", "Success", "Action configuration has been updated successfully.", 3000);
            this.isSaveEnabled = false;
            this.afterUpdateBrandConfig();
          } else {
            if (res && res.data && res.data.error_message) {
              this.toastService.showPopup("ERROR", "Error", res.data.error_message, 3000);
            } else {
              this.toastService.showPopup("ERROR", "Error", "Error while updating General settings.", 3000);
            }
          }
          this.isSaving = false;
        },
        error: (err: any) => {
          console.error('Error saving general configurations:', err);
          this.toastService.showPopup("ERROR", "ERROR", "Error while saving general configurations", 3500);
          this.isSaving = false;
        }
      });
  }


  /**
   * get the latest brand details and update it on helpers and app state
   */
  afterUpdateBrandConfig() {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
      }
    });
  }

  /**
   * Cancel the general configuration changes following reference pattern
   */
  cancelConfiguration() {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.isCancelling) return;

    this.isCancelling = true;

    // Following reference pattern - reload data from database
    // In the reference, cancelActionConfig() calls getBrandDetail() to reload data
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
        
        // Reset form to original values after reloading data
        this.generalForm.patchValue({
          is_action_workflow_enabled: this.brandDetails?.is_action_workflow_enabled || false,
          feed_attachment_enabled: this.brandDetails?.feed_attachment_enabled || false
        });
        
        this.isSaveEnabled = false;
        this.isCancelling = false;
      } else {
        // Fallback: just reset form to current values
        this.generalForm.patchValue({
          is_action_workflow_enabled: this.brandDetails?.is_action_workflow_enabled || false,
          feed_attachment_enabled: this.brandDetails?.feed_attachment_enabled || false
        });
        this.isSaveEnabled = false;
        this.isCancelling = false;
      }
    });
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: "You have made some changes",
            content: "To save your changes, please use the <b>Save</b> button at the top right of the page. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.",
            buttons: [
              { text: "Cancel", type: "secondary", key: "Cancel" },
              { text: "Discard Changes", type: "primary", key: "Discard" }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === "Discard") {
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        // Fallback if Helpers is not available
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}
